import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../api/api";
import {
  User,
  Mail,
  Phone,
  Store,
  Lock,
  Hash,
  Check,
  BarChart,
  Users,
  Wand,
  Eye,
  EyeOff,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import "./Signup.css";

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const dropdownRef = React.useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    companyType: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
    agreeTerms: false,
    marketing: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "" };
    let score = 0;
    if (pwd.length >= 8) score++; // Length
    if (/[A-Z]/.test(pwd)) score++; // Uppercase
    if (/[0-9]/.test(pwd)) score++; // Number
    if (/[^A-Za-z0-9]/.test(pwd)) score++; // Special char
    return {
      score,
      label:
        score <= 1
          ? "Weak"
          : score === 2
          ? "Fair"
          : score === 3
          ? "Good"
          : "Strong",
    };
  };

  const strength = getPasswordStrength(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!form.agreeTerms) {
      setError("You must agree to the Terms of Service");
      return;
    }

    if (strength.score < 2) {
      setError("Password is too weak. Please check requirements.");
      return;
    }

    setLoading(true);

    try {
      // Call backend /register endpoint
      const payload = {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        password: form.password,
        referral_code: form.referralCode || null,
        company_type: form.companyType || null,
      };

      console.log("[Signup] Sending payload:", { ...payload, password: "***" });

      const { data } = await apiClient.post("/register", payload);

      if (data.status === "success") {
        setSuccess(data.message || "Account created successfully! Please check your email to activate your account.");
        setLoading(false);
        // Remove timeout navigate to give user time to read the email instruction
      } else {
        setError(data.message || "Signup failed");
        setLoading(false);
      }
    } catch (err) {
      console.error("[Signup] Error:", err);
      // Extract error message from axios response if available
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to sign up. Please try again.";
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      {/* Left Side (Features) */}
      <div className="signup-left">
        <div className="signup-left-inner">
          <div className="brand-signup-logo">
            shop<span>OS</span>.
          </div>

          <div className="signup-marketing-content">
            <div className="marketing-header">
              <h2>Start Your Journey</h2>
              <p>
                Join thousands of successful shop owners managing their business
                with ShopOS
              </p>
            </div>

            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon-box">
                  <BarChart color="#00c853" size={24} />
                </div>
                <div className="feature-text">
                  <h3>AI-Powered Analytics</h3>
                  <p>
                    Track your shop performance with AI-powered insights and
                    comprehensive metrics
                  </p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon-box">
                  <Users color="#5bb2ff" size={24} />
                </div>
                <div className="feature-text">
                  <h3>Affiliate Management</h3>
                  <p>
                    Connect with top creators and manage your affiliate
                    partnerships seamlessly
                  </p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon-box">
                  <Wand color="#a855f7" size={24} />
                </div>
                <div className="feature-text">
                  <h3>ShopAI Assistant</h3>
                  <p>
                    Get intelligent recommendations and automated insights to
                    grow your business
                  </p>
                </div>
              </div>
            </div>

            <div className="signup-testimonial-card">
              <div className="testimonial-header">
                <div className="avatar">
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&h=100&q=80"
                    alt="Sarah"
                  />
                </div>
                <div className="user-info">
                  <h4>Sarah Johnson</h4>
                  <p>Shop Owner, @sarahjstyle</p>
                </div>
              </div>
              <p className="testimonial-text">
                "ShopOS transformed how I manage my business. The AI insights
                helped me increase revenue by 340% in just 3 months!"
              </p>
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#fbbf24" stroke="none" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side (Form) */}
      <div className="signup-right">
        <div className="signup-box">
          <div className="signup-header">
            <h1>Create Account</h1>
            <p>Fill in your details to get started</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && (
            <div
              className="error-message"
              style={{
                color: "#00c853",
                borderColor: "#00c853",
                background: "rgba(0,200,83,0.1)",
              }}
            >
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="off">
            <div className="form-row">
              <div className="signup-form-group">
                <label>First Name</label>
                <div className="signup-input-wrapper">
                  <User className="signup-input-icon" size={18} />
                  <input
                    type="text"
                    name="firstName"
                    placeholder="John"
                    className="signup-form-input"
                    value={form.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="signup-form-group">
                <label>Last Name</label>
                <div className="signup-input-wrapper">
                  <User className="signup-input-icon" size={18} />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Doe"
                    className="signup-form-input"
                    value={form.lastName}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>

            <div className="signup-form-group">
              <label>Email Address</label>
              <div className="signup-input-wrapper">
                <Mail className="signup-input-icon" size={18} />
                <input
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  className="signup-form-input"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="signup-form-group">
              <label>Account Type</label>
              <div className="signup-custom-dropdown" ref={dropdownRef}>
                <div 
                  className={`signup-dropdown-trigger ${isDropdownOpen ? 'open' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <div className="trigger-left">
                    <Store className="signup-input-icon-static" size={18} />
                    <span 
                      style={{ 
                        color: form.companyType === "" ? "#444" : "#fff",
                        marginLeft: "12px"
                      }}
                    >
                      {form.companyType || "Select account type"}
                    </span>
                  </div>
                  {isDropdownOpen ? (
                    <ChevronUp size={18} className="dropdown-chevron" />
                  ) : (
                    <ChevronDown size={18} className="dropdown-chevron" />
                  )}
                </div>

                {isDropdownOpen && (
                  <div className="signup-dropdown-menu">
                    {["Agency", "Brand"].map((option) => (
                      <div
                        key={option}
                        className={`signup-dropdown-item ${form.companyType === option ? 'selected' : ''}`}
                        onClick={() => {
                          setForm({ ...form, companyType: option });
                          setIsDropdownOpen(false);
                        }}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="signup-form-group">
              <label>Password</label>
              <div className="signup-input-wrapper">
                <Lock className="signup-input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  className="signup-form-input"
                  value={form.password}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="strength-wrapper">
                <div className="password-strength-bar">
                  <div
                    className="strength-indicator"
                    style={{
                      width: `${(strength.score / 4) * 100}%`,
                      background:
                        strength.score <= 1
                          ? "#fe2c55"
                          : strength.score === 2
                          ? "#fbbf24"
                          : "#00c853",
                    }}
                  />
                </div>
                <div className="strength-text">{strength.label}</div>
              </div>
              <div className="password-requirements">
                <div className="requirement-item">
                  <div
                    className={`req-dot ${
                      form.password.length >= 8 ? "met" : ""
                    }`}
                  />
                  At least 8 characters
                </div>
                <div className="requirement-item">
                  <div
                    className={`req-dot ${
                      /[A-Z]/.test(form.password) ? "met" : ""
                    }`}
                  />
                  Contains uppercase and lowercase
                </div>
                <div className="requirement-item">
                  <div
                    className={`req-dot ${
                      /[0-9]/.test(form.password) ? "met" : ""
                    }`}
                  />
                  Contains numbers and symbols
                </div>
              </div>
            </div>

            <div className="signup-form-group">
              <label>Confirm Password</label>
              <div className="signup-input-wrapper">
                <Lock className="signup-input-icon" size={18} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="••••••••"
                  className="signup-form-input"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            <div className="signup-form-group">
              <label>Referral Code (Optional)</label>
              <div className="signup-input-wrapper">
                <Hash className="signup-input-icon" size={18} />
                <input
                  type="text"
                  name="referralCode"
                  placeholder="Enter code"
                  className="signup-form-input"
                  value={form.referralCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="signup-checkbox-group">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={form.agreeTerms}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
                <div className="custom-cb">
                  {form.agreeTerms && (
                    <Check size={14} color="#000" strokeWidth={3} />
                  )}
                </div>
                <span>
                  I agree to the <a href="#">Terms of Service</a> and{" "}
                  <a href="#">Privacy Policy</a>
                </span>
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  name="marketing"
                  checked={form.marketing}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
                <div className="custom-cb">
                  {form.marketing && (
                    <Check size={14} color="#000" strokeWidth={3} />
                  )}
                </div>
                <span>
                  Send me marketing emails about ShopOS features and updates
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="create-account-btn"
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          <div className="signup-login-link">
            Already have an account? <a href="/">Sign In</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
